import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import {
    type InputGroupAddonColorsConstantType,
    type InputGroupAddonDynamicStyleType,
    type InputGroupAddonColors
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    InputGroupSize
} from "../../type";
import {
    concentric
} from "../../../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";

export const INPUT_GROUP_KBD_SIZES: Record<InputGroupSize, "small" | "medium"> = {
    large: "medium",
    medium: "small",
    small: "small"
};

export const getInputGroupAddonColors = ({
    isDisabled,
    colors
}: InputGroupAddonColorsConstantType): InputGroupAddonColors => {
    if(isDisabled) {
        return {
            borderColor: colors.system.state.border.disabled.neutral,
            backgroundColor: "transparent"
        };
    }

    return {
        backgroundColor: colors.system.state.overlay.fill.default,
        borderColor: colors.content.border.subtle
    };
};

const stylesheet = StyleSheet.create({
    attached: {
        alignSelf: "stretch",
        borderStyle: "solid"
    },
    inline: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    attachedContent: {
        flexDirection: "row",
        alignItems: "center",
        flexShrink: 0
    },
    blockStart: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap"
    },
    blockEnd: {
        flexDirection: "row",
        alignItems: "center"
    },
    counter: {
        fontVariant: [
            "tabular-nums"
        ]
    },
    spacer: {
        flex: 1
    }
});

export const useStyles = ({
    isTrailingTextAction,
    controlHeights,
    shapeConfigs,
    touchTargets,
    currentSize,
    pointerType,
    isDisabled,
    radiuses,
    borders,
    variant,
    colors,
    spaces,
    align
}: InputGroupAddonDynamicStyleType) => {
    const currentColors = getInputGroupAddonColors({
        isDisabled,
        colors
    });

    const styles = {
        container: {
            gap: spaces.spacingSm
        } as Mutable<ViewStyle>
    };

    if(align === "blockStart") {
        Object.assign(styles.container, stylesheet.blockStart, {
            paddingHorizontal: currentSize.paddingHorizontal,
            paddingTop: spaces.spacingSm,
            gap: spaces.spacingXs
        });

        return styles;
    }

    if(align === "blockEnd") {
        Object.assign(styles.container, stylesheet.blockEnd, {
            gap: pointerType === "coarse" ? touchTargets.coarse - controlHeights.small : spaces.spacingXs,
            padding: spaces.spacingXs
        });

        return styles;
    }

    if(variant === "inline") {
        Object.assign(styles.container, stylesheet.inline);

        if(isTrailingTextAction) {
            styles.container.marginEnd = -(currentSize.paddingHorizontal - spaces.spacingXs);
        }

        return styles;
    }

    const {
        borderRadius: innerRadius,
        ...innerShape
    } = getCornerStyle({
        radius: concentric({
            outer: radiuses.form,
            inset: borders.line,
            role: radiuses.form
        }),
        shapeConfigs
    });

    Object.assign(styles.container, stylesheet.attached, stylesheet.attachedContent, innerShape, {
        minHeight: currentSize.minHeight - borders.line * 2,
        paddingHorizontal: currentSize.paddingHorizontal,
        backgroundColor: currentColors.backgroundColor,
        borderColor: currentColors.borderColor
    });

    if(align === "start") {
        Object.assign(styles.container, {
            marginStart: -currentSize.paddingHorizontal,
            borderBottomStartRadius: innerRadius,
            borderTopStartRadius: innerRadius,
            borderEndWidth: borders.line
        });

        return styles;
    }

    Object.assign(styles.container, {
        marginEnd: -currentSize.paddingHorizontal,
        borderBottomEndRadius: innerRadius,
        borderTopEndRadius: innerRadius,
        borderStartWidth: borders.line
    });

    return styles;
};
export default stylesheet;
